import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { useCart } from "../context/CartContext";

const ShopProducts = () => {
  const { storeId } = useParams(); // store ID from StoreDetail page
  const navigate = useNavigate();
  const { addToCart } = useCart();

  const [shop, setShop] = useState(null);
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [priceSort, setPriceSort] = useState("none"); // "low" or "high"

  const categories = ["All", "Food", "Electronics", "Clothing", "Home", "Beauty", "Books"];

  // Fetch shop info
  useEffect(() => {
    const fetchShop = async () => {
      try {
        const shopRef = doc(db, "Stores", storeId);
        const snap = await getDoc(shopRef);
        if (snap.exists()) setShop({ id: snap.id, ...snap.data() });
      } catch (error) {
        console.error("Error fetching shop:", error);
      }
    };
    fetchShop();
  }, [storeId]);

  // Fetch all products associated with the shop
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const productsRef = collection(db, "products");
        const q = query(productsRef, where("storeId", "==", storeId));
        const snap = await getDocs(q);

        const data = snap.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          images: doc.data().images || [],
        }));

        setProducts(data);
        setFilteredProducts(data);
      } catch (err) {
        console.error("Error fetching products:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProducts();
  }, [storeId]);

  // Handle search, category, and sorting
  useEffect(() => {
    let filtered = [...products];

    if (searchTerm) {
      filtered = filtered.filter((p) =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (selectedCategory !== "All") {
      filtered = filtered.filter((p) => p.category === selectedCategory);
    }

    if (priceSort === "low") {
      filtered.sort((a, b) => a.price - b.price);
    } else if (priceSort === "high") {
      filtered.sort((a, b) => b.price - a.price);
    }

    setFilteredProducts(filtered);
  }, [searchTerm, selectedCategory, priceSort, products]);

  const handleAddToCart = (product) => {
    addToCart({
      id: product.id,
      name: product.name,
      description: product.description,
      price: product.price,
      image: product.images[0] || product.image || product.imageUrl || "https://via.placeholder.com/300",
      quantity: 1,
    });
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center text-lg">
        Loading...
      </div>
    );

  return (
    <div className="min-h-screen bg-gray-50 sm:p-6 p-3">
      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="px-4 py-2 bg-gray-800 text-white rounded mb-6 hover:bg-gray-700"
      >
        Back
      </button>

      {/* Shop Name */}
      {shop && (
        <h1 className="text-3xl font-bold mb-4 text-gray-800">
          Products from {shop.name}
        </h1>
      )}

      {/* Search, Filter, Price Sort */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
        <input
          type="text"
          placeholder="Search products..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="px-3 py-2 border rounded w-full sm:w-1/2"
        />
        {/* <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="px-3 py-2 border rounded w-full sm:w-1/4"
        >
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {cat}
            </option>
          ))}
        </select> */}
        <select
          value={priceSort}
          onChange={(e) => setPriceSort(e.target.value)}
          className="px-3 py-2 border rounded w-full sm:w-1/4"
        >
          <option value="none">Sort by price</option>
          <option value="low">Low to High</option>
          <option value="high">High to Low</option>
        </select>
      </div>

      {/* Product Grid */}
      {filteredProducts.length > 0 ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {filteredProducts.map((product) => {
            const productImage =
              product.images[0] ||
              product.image ||
              product.imageUrl ||
              "https://via.placeholder.com/300";

            const originalPrice = (product.price * 1.3).toFixed(2); // 30% higher

            return (
              <div
                key={product.id}
                className="bg-white p-1 rounded-lg shadow hover:shadow-lg transition flex flex-col relative"
              >
                {/* 30% Off Badge */}
                <div className="absolute top-2 left-2 bg-red-500 text-white px-2 py-1 text-xs font-semibold rounded">
                  30% OFF
                </div>

                <img
                  src={productImage}
                  alt={product.name}
                  className="w-full h-40 object-cover rounded mb-3 cursor-pointer bg-[#e5e5e5]"
                  onClick={() => navigate(`/product/${product.id}`)}
                />

                <h2 className="font-semibold text-gray-800 text-lg">
                  {product.name}
                </h2>

                <p className="text-gray-600 text-sm line-clamp-2">
                  {product.description}
                </p>

                <div className="flex items-center mt-2 gap-2">
                  <p className="text-indigo-600 font-bold">₹ {product.price}</p>
                  <p className="text-gray-400 line-through text-sm">₹ {originalPrice}</p>
                </div>

                {/* <button
                  onClick={() => handleAddToCart(product)}
                  className="mt-auto bg-yellow-500 hover:bg-yellow-600 text-white py-2 rounded text-sm font-semibold"
                >
                  Add to cart
                </button> */}
              </div>
            );
          })}
        </div>
      ) : (
        <p className="text-gray-600 text-center mt-20 text-lg">
          No products found.
        </p>
      )}
    </div>
  );
};

export default ShopProducts;
